import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "./utils";

// Omit native HTML attributes that conflict with our ReactNode-typed props
// under exactOptionalPropertyTypes (`prefix` is `string | undefined` natively).
export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, hint, error, prefix, suffix, invalid, id, ...props }, ref) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const isInvalid = invalid ?? Boolean(error);

    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={inputId} className="text-xs font-medium text-base-700">
            {label}
          </label>
        )}
        <div
          className={cn(
            "flex h-10 items-center gap-2 rounded-sm border bg-surface px-3",
            // Sprint AQ — semantic motion (see Button.tsx).
            "transition-colors duration-hover ease-hover",
            "focus-within:shadow-focus",
            isInvalid
              ? "border-danger-500 focus-within:border-danger-500"
              : "border-base-300 focus-within:border-ai-500",
            props.disabled && "opacity-50",
          )}
        >
          {prefix && <span className="text-sm text-base-500">{prefix}</span>}
          <input
            id={inputId}
            ref={ref}
            aria-invalid={isInvalid || undefined}
            aria-describedby={cn(hintId, errorId) || undefined}
            className={cn(
              "min-w-0 flex-1 bg-transparent text-md text-base-900 outline-none",
              "placeholder:text-base-400",
              className,
            )}
            {...props}
          />
          {suffix && <span className="text-sm text-base-500">{suffix}</span>}
        </div>
        {error ? (
          <p id={errorId} className="text-xs text-danger-700">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-xs text-base-500">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
